import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, FileCheck, AlertTriangle, Loader2, Network } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';
import { supabase } from '@/integrations/supabase/client';

export function FinalDeclarationsStep() {
  const { t } = useTranslation();
  const { canActivate } = useScrollToActivate();
  const { data, updateData, goToNextStep } = useOnboarding();
  
  // Final confirmation checkboxes
  const [confirmTruthful, setConfirmTruthful] = useState(false);
  const [confirmGDPR, setConfirmGDPR] = useState(false);
  const [confirmCriminalLaw, setConfirmCriminalLaw] = useState(false);

  // BaaS-specific declarations (only required when BaaS service selected)
  const isBaaSApplication = Boolean(data.corporateCompany?.servicesRequested?.includes('baas'));
  const [confirmBaaSRegulatory, setConfirmBaaSRegulatory] = useState(false);
  const [confirmBaaSAML, setConfirmBaaSAML] = useState(false);
  const [confirmBaaSSignatory, setConfirmBaaSSignatory] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Ref for smooth scrolling to validation errors
  const confirmationsRef = useRef<HTMLDivElement>(null);

  // Validation check
  const baasConfirmed = !isBaaSApplication || (confirmBaaSRegulatory && confirmBaaSAML && confirmBaaSSignatory);
  const isAllConfirmed = confirmTruthful && confirmGDPR && confirmCriminalLaw && baasConfirmed;
  const canSubmit = isAllConfirmed;

  // Get user name for display
  const isCorporate = data.accountType === 'corporate';
  const fullName = isCorporate 
    ? data.corporateCompany?.authorizedPerson 
      ? `${data.corporateCompany.authorizedPerson.firstName} ${data.corporateCompany.authorizedPerson.lastName}`
      : t('finalDeclarations.applicant')
    : data.firstName && data.lastName 
      ? `${data.firstName} ${data.lastName}` 
      : data.extractedFirstName && data.extractedLastName
        ? `${data.extractedFirstName} ${data.extractedLastName}`
        : t('finalDeclarations.applicant');

  const entityName = isCorporate ? data.corporateCompany?.name : fullName;

  // Save application data to backend
  const saveApplicationToBackend = async () => {
    try {
      const applicationData = {
        ...data,
        submittedAt: new Date().toISOString(),
      };

      // Use edge function to update session (RLS-safe)
      const { error } = await supabase.functions.invoke('onboarding-session-lookup', {
        body: {
          action: 'submit_application',
          email: data.email,
          onboarding_data: applicationData,
        }
      });

      if (error) {
        console.error('Error saving to backend:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to save application to backend:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setShowValidation(true);
      confirmationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to backend
      await saveApplicationToBackend();
      
      updateData({
        signedDocuments: [
          ...(data.signedDocuments || []),
          'final-declarations'
        ],
        documentsSignedAt: new Date().toISOString(),
      });

      toast({
        title: t('finalDeclarations.submitted'),
        description: t('finalDeclarations.submittedDesc'),
      });

      goToNextStep();
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: t('finalDeclarations.submissionFailed'),
        description: t('common.error'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = () => {
    return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20">
      <ScrollArea className="flex-1">
        <div className="px-4 py-6 max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-4 mx-auto">
              <FileCheck className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">{t('finalDeclarations.title')}</h1>
            <p className="text-muted-foreground">
              {t('finalDeclarations.subtitle')}
            </p>
          </div>

          {/* Application Summary */}
          <div className="bg-muted/30 rounded-xl p-4 mb-6 border border-border">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-accent" />
              <span className="text-xs font-medium text-accent">{t('finalDeclarations.applicationSummary')}</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finalDeclarations.accountType')}:</span>
                <span className="font-medium text-foreground capitalize">{data.accountType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{isCorporate ? t('finalDeclarations.company') : t('finalDeclarations.applicant')}:</span>
                <span className="font-medium text-foreground">{entityName}</span>
              </div>
              {isCorporate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('finalDeclarations.signedBy')}:</span>
                  <span className="font-medium text-foreground">{fullName}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finalDeclarations.date')}:</span>
                <span className="font-medium text-foreground">{formatDate()}</span>
              </div>
            </div>
          </div>

          {/* Confirmation Statements */}
          <div ref={confirmationsRef} className={`bg-muted/20 rounded-xl border p-4 space-y-4 mb-6 transition-colors ${
            showValidation && !isAllConfirmed ? 'border-destructive' : 'border-border'
          }`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-bold text-foreground">{t('finalDeclarations.confirmStatements')}</p>
              {showValidation && !isAllConfirmed && (
                <span className="flex-shrink-0 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                  {t('common.allRequired')}
                </span>
              )}
            </div>
            
            {/* Statement 1: Truthful declaration */}
            <button
              onClick={() => setConfirmTruthful(!confirmTruthful)}
              className="w-full flex items-start gap-3 text-left"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                confirmTruthful ? 'bg-accent' : 'bg-muted/30 border border-border'
              }`}>
                {confirmTruthful && (
                  <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                {t('finalDeclarations.declarations.truthful')}
              </p>
            </button>

            {/* Statement 2: GDPR rights */}
            <button
              onClick={() => setConfirmGDPR(!confirmGDPR)}
              className="w-full flex items-start gap-3 text-left"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                confirmGDPR ? 'bg-accent' : 'bg-muted/30 border border-border'
              }`}>
                {confirmGDPR && (
                  <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                {t('finalDeclarations.declarations.gdpr')}
              </p>
            </button>

            {/* Statement 3: Criminal responsibility */}
            <button
              onClick={() => setConfirmCriminalLaw(!confirmCriminalLaw)}
              className="w-full flex items-start gap-3 text-left"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                confirmCriminalLaw ? 'bg-accent' : 'bg-muted/30 border border-border'
              }`}>
                {confirmCriminalLaw && (
                  <CheckCircle2 className="w-4 h-4 text-accent-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                {t('finalDeclarations.declarations.criminalLaw')}
              </p>
            </button>

            {/* BaaS-specific declarations — only shown when BaaS service selected */}
            {isBaaSApplication && (
              <>
                <div className="border-t border-border pt-3 mt-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Network className="w-4 h-4 text-accent" />
                    <p className="text-xs font-semibold text-accent uppercase tracking-wider">BaaS Partner Declarations</p>
                  </div>
                </div>

                {/* BaaS Declaration 1: Regulatory compliance */}
                <button
                  onClick={() => setConfirmBaaSRegulatory(!confirmBaaSRegulatory)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    confirmBaaSRegulatory ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {confirmBaaSRegulatory && <CheckCircle2 className="w-4 h-4 text-accent-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                    I confirm that the company holds all required regulatory licences and approvals necessary to operate a BaaS/PaaS platform and that these are current and in good standing.
                  </p>
                </button>

                {/* BaaS Declaration 2: AML Policy */}
                <button
                  onClick={() => setConfirmBaaSAML(!confirmBaaSAML)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    confirmBaaSAML ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {confirmBaaSAML && <CheckCircle2 className="w-4 h-4 text-accent-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                    I confirm that the company has a documented AML/CFT policy that complies with applicable laws, that it is reviewed at least annually, and that all staff receive regular AML training.
                  </p>
                </button>

                {/* BaaS Declaration 3: Authorised signatory */}
                <button
                  onClick={() => setConfirmBaaSSignatory(!confirmBaaSSignatory)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    confirmBaaSSignatory ? 'bg-accent' : 'bg-muted/30 border border-border'
                  }`}>
                    {confirmBaaSSignatory && <CheckCircle2 className="w-4 h-4 text-accent-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                    I confirm that I am duly authorised to enter into the BaaS Partner Agreement on behalf of <strong className="text-foreground">{data.corporateCompany?.name || 'the company'}</strong> and that all information provided in this application is accurate and complete.
                  </p>
                </button>
              </>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Submit Button */}
      <div className="sticky bottom-0 bg-background border-t border-border p-4 pb-8">
        {showValidation && !canSubmit && (
          <div className="flex items-center gap-2 mb-3 p-3 bg-destructive/10 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">
              {t('finalDeclarations.pleaseConfirmAll')}
            </p>
          </div>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !canActivate || !canSubmit}
          className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {t('finalDeclarations.submitting')}
            </>
          ) : (
            t('finalDeclarations.submitButton')
          )}
        </Button>
      </div>
    </div>
  );
}
